import React, { useEffect, useId, useMemo, useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { fetchNextPage, useComputeSkuSuggestions, usePricingFetch } from '../../hooks/usePricingFetch';
import {
  buildComputePricingFilter,
  computePriceTypes,
  formatPrice,
  regions,
} from '../../config/pricingConfig';

const MIN_SKU_SEARCH_LENGTH = 2;

const SortIndicator = ({ col, sortKey, sortAsc }) =>
  sortKey === col ? (sortAsc ? ' ↑' : ' ↓') : null;

const ComputePricing = () => {
  const [region, setRegion] = useState('');
  const [sku, setSku] = useState('');
  const [priceType, setPriceType] = useState('');
  const [searchParams, setSearchParams] = useState(null);
  const [extraItems, setExtraItems] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [isSkuFocused, setIsSkuFocused] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const suggestionListId = useId();

  const filter = searchParams
    ? buildComputePricingFilter(searchParams)
    : '';

  const { items, isLoading, error, nextPageLink } = usePricingFetch({
    queryKey: `compute-${filter}`,
    filter,
    enabled: !!searchParams,
  });
  const {
    suggestions: skuSuggestions,
    isLoading: isLoadingSuggestions,
    error: skuSuggestionsError,
  } = useComputeSkuSuggestions(sku);

  const canSearchSuggestions = sku.trim().length >= MIN_SKU_SEARCH_LENGTH;
  const showSuggestionDropdown = isSkuFocused && canSearchSuggestions;

  useEffect(() => {
    if (!showSuggestionDropdown || skuSuggestions.length === 0) {
      setActiveSuggestionIndex(-1);
      return;
    }

    setActiveSuggestionIndex(0);
  }, [showSuggestionDropdown, skuSuggestions]);

  const handleFetch = () => {
    const trimmedSku = sku.trim();
    setSku(trimmedSku);
    setSearchParams({ region, sku: trimmedSku, priceType });
    setExtraItems([]);
    setNextPageUrl(null);
    setIsSkuFocused(false);
  };

  const handleLoadMore = async () => {
    const url = nextPageUrl || nextPageLink;
    if (!url) return;
    setLoadingMore(true);
    setLoadMoreError(null); // Clear previous error
    try {
      const data = await fetchNextPage(url);
      setExtraItems((prev) => [...prev, ...(data.Items || [])]);
      setNextPageUrl(data.NextPageLink || null);
    } catch (err) {
      setLoadMoreError('Failed to load more results.');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const handleSkuSelect = (selectedSku) => {
    setSku(selectedSku);
    setIsSkuFocused(false);
    setActiveSuggestionIndex(-1);
  };

  const handleSkuKeyDown = (event) => {
    if (!showSuggestionDropdown) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestionIndex((currentIndex) => (
        skuSuggestions.length === 0
          ? -1
          : (currentIndex + 1 + skuSuggestions.length) % skuSuggestions.length
      ));
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestionIndex((currentIndex) => (
        skuSuggestions.length === 0
          ? -1
          : (currentIndex - 1 + skuSuggestions.length) % skuSuggestions.length
      ));
    }

    if (event.key === 'Enter' && activeSuggestionIndex >= 0 && skuSuggestions[activeSuggestionIndex]) {
      event.preventDefault();
      handleSkuSelect(skuSuggestions[activeSuggestionIndex]);
    }

    if (event.key === 'Escape') {
      setIsSkuFocused(false);
      setActiveSuggestionIndex(-1);
    }
  };

  const allItems = useMemo(() => [...items, ...extraItems], [items, extraItems]);
  const sortedItems = useMemo(() => 
    sortKey 
      ? [...allItems].sort((a, b) => {
          const va = a[sortKey]; const vb = b[sortKey];
          return sortAsc
            ? (typeof va === 'number' ? va - vb : String(va).localeCompare(String(vb)))
            : (typeof vb === 'number' ? vb - va : String(vb).localeCompare(String(va)));
        })
      : allItems, 
  [allItems, sortKey, sortAsc]
  );

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Compute Pricing</h2>
      <p className="text-sm text-gray-500 mb-4">
        Enter an Azure VM SKU name (e.g.{' '}
        <code className="bg-gray-100 px-1 rounded">Standard_D2s_v3</code>,{' '}
        <code className="bg-gray-100 px-1 rounded">Standard_E4s_v5</code>,{' '}
        <code className="bg-gray-100 px-1 rounded">Standard_B2ms</code>) and select a region.
      </p>
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <Select value={region} onValueChange={setRegion}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Select Region" /></SelectTrigger>
          <SelectContent>{regions.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative w-full sm:w-64">
          <Input
            value={sku}
            onChange={(e) => {
              setSku(e.target.value);
              setIsSkuFocused(true);
            }}
            onFocus={() => setIsSkuFocused(true)}
            onBlur={(e) => {
              if (e.relatedTarget?.dataset?.skuSuggestion === 'true') return;
              setIsSkuFocused(false);
              setActiveSuggestionIndex(-1);
            }}
            onKeyDown={handleSkuKeyDown}
            placeholder="SKU (e.g. Standard_D2s_v3)"
            className="w-full"
            autoComplete="off"
            aria-autocomplete="list"
            aria-controls={suggestionListId}
            aria-expanded={showSuggestionDropdown}
          />
          {showSuggestionDropdown && (
            <div
              id={suggestionListId}
              role="listbox"
              className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg"
            >
              {isLoadingSuggestions && (
                <div className="px-3 py-2 text-sm text-gray-500">Finding SKUs…</div>
              )}
              {!isLoadingSuggestions && skuSuggestions.map((suggestion, index) => (
                <button
                  key={suggestion}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  data-sku-suggestion="true"
                  aria-selected={index === activeSuggestionIndex}
                  className={`block w-full px-3 py-2 text-left text-sm ${
                    index === activeSuggestionIndex ? 'bg-gray-100 text-gray-900' : 'text-gray-700'
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSkuSelect(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
              {!isLoadingSuggestions && skuSuggestions.length === 0 && !skuSuggestionsError && (
                <div className="px-3 py-2 text-sm text-gray-500">No matching SKUs found.</div>
              )}
              {!isLoadingSuggestions && skuSuggestionsError && (
                <div className="px-3 py-2 text-sm text-red-500">Unable to load SKU suggestions.</div>
              )}
            </div>
          )}
        </div>
        <Select value={priceType} onValueChange={setPriceType}>
          <SelectTrigger className="w-full sm:w-[200px]"><SelectValue placeholder="Price Type" /></SelectTrigger>
          <SelectContent>
            {computePriceTypes.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={handleFetch} disabled={isLoading || !region || !sku.trim() || !priceType}>
          {isLoading ? 'Loading…' : 'Fetch Prices'}
        </Button>
      </div>

      {error && <div className="text-red-500 mb-4 text-sm">{error.message}</div>}
      {isLoading && <SkeletonTable rows={5} cols={3} />}

      {!isLoading && sortedItems.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-2">Showing {sortedItems.length} result(s)</p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('skuName')}>SKU Name<SortIndicator col="skuName" sortKey={sortKey} sortAsc={sortAsc} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('productName')}>Product<SortIndicator col="productName" sortKey={sortKey} sortAsc={sortAsc} /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => handleSort('unitPrice')}>Unit Price<SortIndicator col="unitPrice" sortKey={sortKey} sortAsc={sortAsc} /></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedItems.map((price) => (
                <TableRow key={`${price.skuId}-${price.meterName}`}>
                  <TableCell>{price.skuName}</TableCell>
                  <TableCell>{price.productName}</TableCell>
                  <TableCell>{formatPrice(price.unitPrice, price.currencyCode, price.unitOfMeasure)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {(nextPageUrl || nextPageLink) && (
            <div className="mt-4 text-center">
              {loadMoreError && <div className="text-red-500 mb-2 text-sm">{loadMoreError}</div>}
              <Button onClick={handleLoadMore} disabled={loadingMore} className="bg-gray-100 text-gray-700 hover:bg-gray-200">
                {loadingMore ? 'Loading…' : 'Load More'}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ComputePricing;